'use client';

import { isAfter } from 'date-fns';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Category } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import LiveIndicator from '@/components/LiveIndicator';
import { RecentGames } from '@/components/RecentGames';
import { Skeleton } from '@/components/Skeleton';
import {
  CategoriesSliderSkeleton,
  PaymentsSliderSkeleton,
  ProvidersSliderSkeleton,
} from '@/components/skeletons';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { parseSoftSwissFormatDate } from '@/helpers/dateTime';
import { getGameCategoriesTransMap } from '@/helpers/game';
import {
  BONUS_BUY_SLIDER_PARAMS,
  HITS_SLIDER_PARAMS,
  HOT_AND_COLD_SLIDER_PARAMS,
  INSTANT_WINS_SLIDER_PARAMS,
  NEW_GAMES_SLIDER_PARAMS,
  oneRowSliderLiveCasinoParams,
  sliderTopCategoryParams,
  TOP_VIP_SLIDER_PARAMS,
} from '@/helpers/games';
import { isFrenchLocale } from '@/helpers/languages';
import { COMMON_VIP_GROUPS, groupMember, USER_GROUP } from '@/helpers/user';
import { useUserWageringBonuses } from '@/hooks/bonuses';
import { useNavigation } from '@/hooks/navigation';
import { useResetPromoStore } from '@/hooks/useResetPromoStore';
import { useGetCommonStore } from '@/store';
import { ScContainer, ScLobbySliderWrapper, Spinner } from '@/styles/common';
import { LazyLoad } from '../common';
import { useCMSActiveTournaments } from '../tournaments';
import { GameCategorySlider } from './components';
import { GamesSliderExclusive } from './components/GamesSliderExclusive';
import { HotAndCold } from './components/HotAndCold';
import { useUserCurrency } from './components/HotAndCold/hooks/useUserCurrency';
import LobbyGamesSlider from './components/LobbyGamesSlider';
import { MIN_TOURNAMENTS_TO_DISPLAY_SLIDER } from './components/TournamentsSlider/helpers';
import { WageringGamesSlider } from './components/WageringGamesSlider';

const SHOW_HITS_FROM = '2025-04-14 00:00:00 UTC';

export const LobbyPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');

  const { mainColor, blackColor } = useTheme();

  const { generateLink } = useNavigation();
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const user = useCommonStore.use.user();
  const pendingUser = useCommonStore.use.pendingUser();
  const { activeTournaments } = useCMSActiveTournaments();

  const getSliderTitle = (category: Category) =>
    `${getGameCategoriesTransMap(category, t)}${
      isFrenchLocale(language) ? '' : ` ${t('game.games')}`
    }`;

  useResetPromoStore();

  const PaymentsSlider = dynamic(() => import('./components/PaymentsSlider'), {
    loading: () => <PaymentsSliderSkeleton />,
    ssr: false,
  });

  const ProvidersSlider = dynamic(
    () => import('./components/ProvidersSlider'),
    {
      loading: () => <ProvidersSliderSkeleton />,
      ssr: false,
    }
  );

  const CategoriesSlider = dynamic(
    () => import('./components/CategoriesSlider'),
    {
      loading: () => <CategoriesSliderSkeleton />,
      ssr: false,
    }
  );

  const StarWinners = dynamic(() => import('./components/StarWinners'), {
    loading: () => (
      <Spinner background={blackColor} color={mainColor} size={50} />
    ),
    ssr: false,
  });

  const JackpotCategory = dynamic(
    () => import('@/modules/jackpot/components/JackpotCategory'),
    {
      loading: () => <GamesSkeleton withTitle />,
      ssr: false,
    }
  );

  const TopicsSlider = dynamic(() => import('./components/TopicsSlider'), {
    loading: () => <ProvidersSliderSkeleton />,
    ssr: false,
  });

  const RandomizeSlider = dynamic(
    () => import('@/components/RandomizeSlider'),
    {
      loading: () => (
        <Spinner background={blackColor} color={mainColor} size={50} />
      ),
      ssr: false,
    }
  );

  const MobileAppPromoBanner = dynamic(
    () => import('@/modules/mobileApp/components/MobileAppPromoBanner'),
    {
      loading: () => <Skeleton.Rect height={120} />,
      ssr: false,
    }
  );

  const JackpotBanner = dynamic(
    () => import('@/modules/jackpot/components/JackpotBanner'),
    {
      loading: () => <Skeleton.Rect height={128} />,
      ssr: false,
    }
  );

  const PromotionsCarousel = dynamic(
    () => import('./components/PromotionsCarousel'),
    {
      loading: () => <GamesSkeleton withTitle />,
      ssr: false,
    }
  );

  const TournamentsSlider = dynamic(
    () => import('./components/TournamentsSlider'),
    {
      loading: () => <GamesSkeleton withTitle />,
      ssr: false,
    }
  );

  const LobbyLoyaltySection = dynamic(
    () => import('./components/LobbyLoyaltySection'),
    {
      ssr: false,
    }
  );

  const shouldShowTournamentsSlider =
    activeTournaments.length >= MIN_TOURNAMENTS_TO_DISPLAY_SLIDER;

  const { hasWageringBonus } = useUserWageringBonuses();

  const isVIP = useMemo(() => {
    if (pendingUser) return false;
    return COMMON_VIP_GROUPS.some((group) => groupMember(group)(user));
  }, [user, pendingUser]);

  const showHits = useMemo(() => {
    if (pendingUser) return false;

    const isCreated5thDeposit = groupMember(USER_GROUP.DEP5)(user);
    return (
      !(isVIP || isCreated5thDeposit) &&
      isAuthenticated &&
      user?.createdAt &&
      isAfter(
        parseSoftSwissFormatDate(user.createdAt),
        parseSoftSwissFormatDate(SHOW_HITS_FROM)
      )
    );
  }, [isVIP, user, isAuthenticated, pendingUser]);

  const { currency } = useUserCurrency();

  return (
    <>
      <ScContainer data-testid="lobby-page">
        <ScLobbySliderWrapper data-testid="lobby-slider-wrapper">
          {isAuthenticated && <RecentGames />}
          {hasWageringBonus && <WageringGamesSlider />}
          {showHits && (
            <LobbyGamesSlider
              categoryId={Category.HITS}
              sliderParams={HITS_SLIDER_PARAMS}
              title={t(`common:gameCategories:${Category.HITS}`)}
            />
          )}
          <GamesSliderExclusive />
          {isVIP && (
            <LobbyGamesSlider
              categoryId={Category.TOP_VIP}
              sliderParams={TOP_VIP_SLIDER_PARAMS}
              title={t(`common:gameCategories:${Category.TOP_VIP}`)}
            />
          )}
          <LazyLoad cacheKey="TopGames">
            <LobbyGamesSlider
              categoryId={Category.TOP}
              sliderParams={sliderTopCategoryParams}
              title={getSliderTitle(Category.TOP)}
              titleIcon={
                <ImageComponent
                  alt="icon"
                  height={24}
                  src="/topIcon.webp"
                  width={24}
                />
              }
            />
          </LazyLoad>

          <LazyLoad cacheKey="LoyaltySection">
            <LobbyLoyaltySection />
          </LazyLoad>

          {/* only for testing purposes */}
          <Link
            href={generateLink('/game/slotopia/level-up-mega-fruit?demo=true')}
            id="testDemoGame"
            style={{ display: 'none' }}
          />
          {!isAuthenticated && (
            <LazyLoad cacheKey="PromotionsCarousel">
              <PromotionsCarousel />
            </LazyLoad>
          )}
          <LazyLoad cacheKey="GAME_CATEGORY_SLIDER">
            <GameCategorySlider />
          </LazyLoad>
          <LazyLoad cacheKey="NEW">
            <LobbyGamesSlider
              categoryId={Category.NEW}
              sliderParams={NEW_GAMES_SLIDER_PARAMS}
              title={getSliderTitle(Category.NEW)}
            />
          </LazyLoad>

          {shouldShowTournamentsSlider && (
            <LazyLoad cacheKey="Tournaments">
              <TournamentsSlider />
            </LazyLoad>
          )}

          <LazyLoad cacheKey="CASUAL">
            <LobbyGamesSlider
              categoryId={Category.CASUAL}
              sliderParams={INSTANT_WINS_SLIDER_PARAMS}
              title={t(`common:gameCategories:${Category.INSTANT_WINS}`)}
              titleIcon={
                <ImageComponent
                  alt="icon"
                  height={24}
                  src="/instantIcon.webp"
                  width={24}
                />
              }
            />
          </LazyLoad>

          <LazyLoad cacheKey="JackpotBanner">
            <JackpotBanner />
          </LazyLoad>

          <LazyLoad cacheKey="BONUS_BUY">
            <LobbyGamesSlider
              categoryId={Category.BONUS_BUY}
              sliderParams={BONUS_BUY_SLIDER_PARAMS}
              title={t(`common:gameCategories:${Category.BONUS_BUY}`)}
            />
          </LazyLoad>

          {!!currency && (
            <LazyLoad cacheKey="HotAndCold">
              <HotAndCold
                sliderParams={HOT_AND_COLD_SLIDER_PARAMS}
                title={t(`common:gameCategories:${Category.HOT_AND_COLD}`)}
                titleIcon={
                  <ImageComponent
                    alt="icon"
                    height={24}
                    src="/hot_and_cold.webp"
                    width={24}
                  />
                }
              />
            </LazyLoad>
          )}
          <LazyLoad cacheKey="JackpotCategory">
            <JackpotCategory />
          </LazyLoad>
        </ScLobbySliderWrapper>
      </ScContainer>

      <LazyLoad cacheKey="RandomizeSlider">
        <RandomizeSlider />
      </LazyLoad>

      <ScContainer>
        <ScLobbySliderWrapper>
          <LazyLoad cacheKey="LiveGames">
            <LobbyGamesSlider
              categoryId={Category.LIVE}
              sliderParams={oneRowSliderLiveCasinoParams}
              title={`${t(`common:gameCategories:${Category.LIVE}`)}${t(
                'casino'
              )}`}
              titleIcon={<LiveIndicator />}
            />
          </LazyLoad>
          <LazyLoad cacheKey="TopicsSlider">
            <TopicsSlider />
          </LazyLoad>
          <LazyLoad cacheKey="StarWinners">
            <StarWinners />
          </LazyLoad>
          <LazyLoad cacheKey="MobileAppPromoBanner">
            <MobileAppPromoBanner />
          </LazyLoad>
          <LazyLoad cacheKey="CategoriesSlider">
            <CategoriesSlider />
          </LazyLoad>
          <LazyLoad cacheKey="ProvidersSlider">
            <ProvidersSlider />
          </LazyLoad>

          <LazyLoad cacheKey="PaymentsSlider">
            <PaymentsSlider />
          </LazyLoad>
        </ScLobbySliderWrapper>
      </ScContainer>
    </>
  );
};
